import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { fetchThemeData } from "../../admin/themeSlice";
import { resolveCrossroadsTheme, themeCssVars } from "../../utils/crossroadsTheme";
import { useLogoBacking } from "../../utils/useHeaderOffset";
import "./Rules.css";

/* Step icons, in the order of the default rules: read → choose → see the outcome → finish. */
function SignpostIcon() {
  return (
    <svg viewBox="0 0 64 64" aria-hidden>
      <rect x="29" y="6" width="6" height="52" rx="2" fill="#1b2a49" />
      <path d="M12 14h34l8 7-8 7H12z" fill="#1b2a49" />
      <path d="M52 32H18l-8 7 8 7h34z" fill="#1b2a49" />
    </svg>
  );
}
function ForkIcon() {
  return (
    <svg viewBox="0 0 64 64" aria-hidden>
      <path d="M32 58V40c0-8-6-14-14-18" fill="none" stroke="#1F6FFF" strokeWidth="7" strokeLinecap="round" />
      <path d="M8 16l14-4-2 14z" fill="#1F6FFF" />
      <path d="M32 40c0-8 6-14 14-18" fill="none" stroke="#FF3B1F" strokeWidth="7" strokeLinecap="round" />
      <path d="M56 16l-14-4 2 14z" fill="#FF3B1F" />
    </svg>
  );
}
function PlayIcon() {
  return (
    <svg viewBox="0 0 64 64" aria-hidden>
      <circle cx="32" cy="32" r="26" fill="#1b2a49" />
      <path d="M26 20l18 12-18 12z" fill="#ffffff" />
    </svg>
  );
}
function FlagIcon() {
  return (
    <svg viewBox="0 0 64 64" aria-hidden>
      <defs>
        <pattern id="rules-check" width="12" height="12" patternUnits="userSpaceOnUse">
          <rect width="12" height="12" fill="#ffffff" />
          <rect width="6" height="6" fill="#1b2a49" />
          <rect x="6" y="6" width="6" height="6" fill="#1b2a49" />
        </pattern>
      </defs>
      <rect x="8" y="6" width="5" height="54" rx="2" fill="#1b2a49" />
      <path d="M13 9c10-5 18 5 28 0s14-2 16 0v28c-2-2-6-5-16 0s-18-5-28 0z" fill="url(#rules-check)" stroke="#1b2a49" strokeWidth="2.5" />
    </svg>
  );
}
const ICONS = [SignpostIcon, ForkIcon, PlayIcon, FlagIcon];

/** How to Play, between the start screen and the game. */
export default function Rules() {
  useLogoBacking();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth?.user);
  const { status: themeStatus, data: themeData } = useSelector((s) => s.theme);

  useEffect(() => {
    if (themeStatus === "idle") dispatch(fetchThemeData({ themeId: null }));
  }, [themeStatus, dispatch]);

  const theme = resolveCrossroadsTheme(themeData);
  const next = () => navigate(theme.mode === "live" ? "/vote" : "/play", { replace: true });

  if (!user) {
    return (
      <div className="cr-root rules-page">
        <p className="cr-error">Please open Crossroads from your game link.</p>
      </div>
    );
  }

  return (
    <div className="cr-root rules-page" style={themeCssVars(theme)}>
      <section className="rules-panel" aria-labelledby="rules-title">
        <h1 id="rules-title" className="rules-title">
          How to Play
        </h1>
        <ol className="rules-steps">
          {theme.rules.map((rule, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <li key={i} className="rules-step">
                <span className="rules-icon">
                  <Icon />
                </span>
                <span className="rules-text">{rule}</span>
              </li>
            );
          })}
        </ol>
        <button type="button" className="rules-next" onClick={next}>
          Next <span aria-hidden>→</span>
        </button>
      </section>
    </div>
  );
}
